import React from "react";
import classNames from "classnames";
import ClipLoader from 'react-spinners/ClipLoader';
import Framework from "@beardeddevops/react.framework";
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import PropTypes from "prop-types";
import TextField from "@material-ui/core/TextField/index";
import {
	buildDenomPayload,
	computeCounted,
	DENOMINATIONS,
	emptyDenomCounts,
	formatMoney,
	parseCustomizationBool,
	parseDenominations,
} from "utils/registerDenominations";

function registerSettingsFromProps(props) {
	const hasRetainedFloat = props.retainedFloat != null
		&& props.retainedFloat !== ''
		&& !Number.isNaN(parseFloat(props.retainedFloat));
	return {
		auditDenominations: parseCustomizationBool(props.registerAuditDenominations, false),
		requireVarianceNote: parseCustomizationBool(props.registerRequireVarianceNote, false),
		retainedFloat: hasRetainedFloat ? parseFloat(props.retainedFloat) : null,
		defaultDenomSpread: props.registerDefaultDenominations || null,
	};
}

export default class RegisterLedgerCount extends Framework.Components.Object {
	static propTypes = {
		currentAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		registerAuditDenominations: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
		registerRequireVarianceNote: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
		registerDefaultDenominations: PropTypes.string,
		retainedFloat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		preflightMethod: PropTypes.oneOf(['sod', 'eod']),
		onCountComplete: PropTypes.func,
	};

	constructor(props) {
		super(props, Types.Registers_Ledgers._name);
		const settings = registerSettingsFromProps(props);
		// Default model pkey is 0, which isValidPkey treats as an existing row → PUT + required note.
		this.state.pkey = null;
		this.update = API.RequestType.CREATE;
		this.state.type = true;
		this.state.method = props.preflightMethod ? 'count-preflight' : 'count';
		this.state.amount = '0.00';
		this.state.denomCounts = emptyDenomCounts();
		this.state.counted_amount = '';
		this.state.user_note = '';
		this.state.counted_amount_error = '';
		this.state.user_note_error = '';
		this.state.denomValidationError = '';
		this.state.auditDenominations = settings.auditDenominations;
		this.state.requireVarianceNote = settings.requireVarianceNote;
		this.state.retainedFloat = settings.retainedFloat;
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		super.componentDidUpdate(prevProps, prevState, snapshot);
		this.update = API.RequestType.CREATE;
	}

	handleProcess = (event) => (
		API.Validator.process(this, API.RequestType.CREATE, true, event ? event.target.name : null)
	);

	getExpected = () => parseFloat(this.props.currentAmount) || 0;

	getCounted = () => {
		if (this.state.auditDenominations) {
			return computeCounted(this.state.denomCounts);
		}
		const parsed = parseFloat(this.state.counted_amount);
		return Number.isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100;
	};

	getVariance = () => Math.round((this.getCounted() - this.getExpected()) * 100) / 100;

	handleDenomChange = (key) => (event) => {
		const raw = event.currentTarget.value;
		const counts = {...this.state.denomCounts, [key]: raw === '' ? '0' : raw};
		this.setState({
			denomCounts: counts,
			denominations: JSON.stringify(buildDenomPayload(counts)),
			denomValidationError: '',
			counted_amount_error: '',
		});
	};

	handleCountedAmountUpdate = (name, value) => {
		if (value != null && value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) {
			return;
		}
		this.handleTextFieldPropertyUpdate(name, value);
		if (this.state.counted_amount_error || this.state.denomValidationError) {
			this.setState({counted_amount_error: '', denomValidationError: ''});
		}
	};

	handleUserNoteUpdate = (name, value) => {
		this.handleTextFieldPropertyUpdate(name, value);
		if (this.state.user_note_error) {
			this.setState({user_note_error: ''});
		}
	};

	handleCountedAmountBlur = () => {
		if (this.state.counted_amount === '' || this.state.counted_amount == null) {
			return;
		}
		const parsed = parseFloat(this.state.counted_amount);
		if (!Number.isNaN(parsed)) {
			this.setState({counted_amount: formatMoney(parsed)});
		}
	};

	buildCountResult = () => {
		const counted = this.getCounted();
		return {
			expectedBefore: this.getExpected(),
			counted,
			variance: this.getVariance(),
			denomCounts: {...this.state.denomCounts},
			denominations: JSON.stringify(buildDenomPayload(this.state.denomCounts)),
			counted_amount: formatMoney(counted),
			user_note: String(this.state.user_note || '').trim(),
		};
	};

	setFieldError = (field, message) => {
		API.Validator.constructor.process_error(this, field, message);
	};

	validateCount = () => {
		const properties = this.model.properties;
		let valid = true;
		const counted = this.getCounted();

		if (counted < 0.005) {
			const continueLabel = this.props.preflightMethod
				? (this.props.preflightMethod === 'eod' ? 'End of Day' : 'Start of Day')
				: 'Count';
			if (!window.confirm(`Counted cash is $0.00. Continue with this ${continueLabel}?`)) {
				this.setState({loading: false, denomValidationError: ''});
				return false;
			}
		}

		if (properties.counted_amount?.validate) {
			this.setFieldError('counted_amount', properties.counted_amount.validate(this.state.counted_amount));
		} else {
			this.setFieldError('counted_amount', '');
		}

		const variance = this.getVariance();
		const noteRequired = this.state.requireVarianceNote && Math.abs(variance) >= 0.005;
		const userNote = String(this.state.user_note || '').trim();
		const noteMessage = 'A note is required to reconcile a non-zero cash variance.';

		if (noteRequired && !userNote) {
			this.setFieldError('user_note', noteMessage);
			valid = false;
		} else if (properties.user_note?.validate) {
			this.setFieldError('user_note', properties.user_note.validate(userNote));
		} else {
			this.setFieldError('user_note', '');
		}

		this.setState({
			loading: false,
			denomValidationError: '',
		});

		return valid;
	};

	handleSubmit = async (ev) => {
		if (ev && typeof ev.preventDefault === 'function') {
			ev.preventDefault();
		}

		if (!this.validateCount()) {
			return;
		}

		const countResult = this.buildCountResult();

		if (this.props.preflightMethod && typeof this.props.onCountComplete === 'function') {
			this.setState({loading: false}, () => {
				this.props.onCountComplete(countResult, this.props.preflightMethod);
			});
			return;
		}

		await new Promise((resolve) => {
			this.setState({
				method: 'count',
				amount: '0.00',
				event: 'Count',
				note: 'Count',
				counted_amount: countResult.counted_amount,
				denominations: this.state.auditDenominations ? countResult.denominations : '',
				user_note: countResult.user_note,
				counted_amount_error: '',
				user_note_error: '',
				denomValidationError: '',
			}, resolve);
		});

		this.update = API.RequestType.CREATE;
		await super.handleSubmit(ev);
	};

	renderReadOnlyAmountField = (label, value) => (
		<TextField
			label={label}
			margin={"dense"}
			variant="outlined"
			value={formatMoney(value)}
			fullWidth
			disabled
			InputProps={{startAdornment: <i className="fa fa-usd" style={{marginRight: 5}}/>}}
		/>
	);

	renderDenominations = () => {
		const rows = [];
		for (let i = 0; i < DENOMINATIONS.length; i += 6) {
			rows.push(DENOMINATIONS.slice(i, i + 6));
		}
		return rows.map((denoms, rowIndex) => (
			<div className={"row"} key={rowIndex}>
				{denoms.map(d => (
					<div className={"col-2"} key={d.key} style={{marginBottom: 10}}>
						<TextField
							type={"number"}
							label={d.label}
							margin={"dense"}
							variant="outlined"
							value={this.state.denomCounts[d.key] ?? '0'}
							onChange={this.handleDenomChange(d.key)}
							inputProps={{min: 0}}
							fullWidth={true}
						/>
					</div>
				))}
			</div>
		));
	};

	render() {
		const object = this.state;
		const counted = this.getCounted();
		const variance = this.getVariance();
		const noteRequired = this.state.requireVarianceNote && Math.abs(variance) >= 0.005;

		return (
			<div>
				<form>
					<fieldset>
						{this.state.auditDenominations ?
							<>
								<h4 className="section-title">Register Denominations</h4>
								{this.renderDenominations()}
								{object.denomValidationError &&
									<div className="text-danger" style={{marginBottom: 8}}>
										{object.denomValidationError}
									</div>}
							</>
							:
							<div className="row">
								<div className="col-md-6">
									<Framework.Elements.DollarAmount
										name={"counted_amount"}
										label={"Counted Cash in Drawer"}
										value={object.counted_amount}
										error={object.counted_amount_error}
										property={this.model.properties.counted_amount}
										update={this.handleCountedAmountUpdate}
										handleBlur={this.handleCountedAmountBlur}
										margin={"dense"}
									/>
								</div>
							</div>}
						<div className="row" style={{marginTop: 8}}>
							<div className="col-3">
								{this.renderReadOnlyAmountField('Expected Cash in Drawer', this.getExpected())}
							</div>
							<div className="col-3">
								{this.renderReadOnlyAmountField('Counted Cash in Drawer', counted)}
							</div>
							<div className="col-3">
								{this.renderReadOnlyAmountField('Counted Variance', variance)}
							</div>
						</div>
						<div className="form-group" style={{marginTop: 8}}>
							<Framework.Elements.TextField
								name={"user_note"}
								label={noteRequired ? "Variance Note (required)" : "Note (optional)"}
								property={this.model.properties.user_note}
								value={object.user_note}
								error={object.user_note_error}
								update={this.handleUserNoteUpdate}
								multiline={true}
							/>
						</div>
					</fieldset>
				</form>
				<div className={classNames("loader", {'active': object.loading})}>
					<ClipLoader color={"#4A4A4A"} loading={object.loading}/>
				</div>
			</div>
		);
	}
}

export function resolveCashLeftFromCount(countResult, settings, method, defaultDenomSpread) {
	const {auditDenominations, retainedFloat} = settings;
	if (auditDenominations) {
		if (defaultDenomSpread) {
			return computeCounted(parseDenominations(defaultDenomSpread));
		}
		return countResult.counted;
	}
	if (retainedFloat != null) {
		return retainedFloat;
	}
	return countResult.counted;
}

export function resolveDayDenomCounts(countResult, settings, method, defaultDenomSpread) {
	if (!settings.auditDenominations) {
		return emptyDenomCounts();
	}
	if (defaultDenomSpread) {
		return parseDenominations(defaultDenomSpread);
	}
	if (method === 'eod') {
		return {...countResult.denomCounts};
	}
	return emptyDenomCounts();
}
